import React from "react";
import { Container } from "../../HtmlTags/Container/Container";
import { CustomDropdown } from "../../CustomDropdown/CustomDropdown";
import { DistanceRangeSlider } from "../../DistanceRangeSlider/DistanceRangeSlider";
import { FiltersProps } from "../types/types";
import { SHARE_LABELS } from "../../../../utils/constants";
import { Button } from "../../HtmlTags/Button/Button";

export const Filters: React.FC<FiltersProps> = ({
  maxDistance,
  setMaxDistance,
  categoryOptions,
  conditionOptions,
  selectedCategory = [],
  selectedCondition = [],
  onCategoryChange,
  onConditionChange,
  onResetFilters,
  hasActiveFilters = false,
}) => {
  const handleCategorySelect = (value: string) => {
    if (onCategoryChange) {
      const newCategories = selectedCategory.includes(value)
        ? selectedCategory.filter((cat) => cat !== value)
        : [...selectedCategory, value];
      onCategoryChange(newCategories);
    }
  };

  const handleConditionSelect = (value: string) => {
    if (onConditionChange) {
      const newConditions = selectedCondition.includes(value)
        ? selectedCondition.filter((cond) => cond !== value)
        : [...selectedCondition, value];
      onConditionChange(newConditions);
    }
  };

  return (
    <>
      <Container className="filter-item">
        <label className="filter-label" id="search-filter-category-label">
          {SHARE_LABELS.FILTER_OPTIONS.CATEGORY}
        </label>
        <CustomDropdown
          options={categoryOptions}
          placeholder={SHARE_LABELS.FILTER_OPTIONS.ALL_CATEGORIES}
          selectedValues={selectedCategory}
          onSelect={handleCategorySelect}
          className="second-row-dropdown"
          multiple={true}
          ariaLabel={SHARE_LABELS.FILTER_OPTIONS.CATEGORY}
          getPopupContainer={() => document.body}
        />
      </Container>

      <Container className="filter-item">
        <label className="filter-label" id="search-filter-condition-label">
          {SHARE_LABELS.FILTER_OPTIONS.CONDITION}
        </label>
        <CustomDropdown
          options={conditionOptions}
          placeholder={SHARE_LABELS.FILTER_OPTIONS.ALL_CONDITIONS}
          selectedValues={selectedCondition}
          onSelect={handleConditionSelect}
          className="second-row-dropdown"
          multiple={true}
          ariaLabel={SHARE_LABELS.FILTER_OPTIONS.CONDITION}
          getPopupContainer={() => document.body}
        />
      </Container>

      <Container className="filter-item filter-slider">
        <label className="filter-label">Distance</label>
        <DistanceRangeSlider
          min={10}
          max={50}
          value={maxDistance}
          onChange={(val) => {
            if (typeof val === 'number') {
              setMaxDistance(Math.max(10, val));
            }
          }}
          className="distance-slider"
          singleValue={true}
        />
      </Container>
      <Container className="filter-item filter-reset">
        <Button
          palette="secondary"
          size="small"
          colorScheme="medium"
          hasIcon={false}
          onClick={onResetFilters}
          disabled={!hasActiveFilters}
          className="filter-reset-btn"
        >
          {SHARE_LABELS.FILTER_OPTIONS.RESET}
        </Button>
      </Container>
    </>
  );
};
/**
 * Filters component for item search and filtering functionality
 *
 * This component renders a set of filter controls including category dropdown,
 * condition dropdown, and distance range slider. It provides a user interface
 * for filtering items based on various criteria.
 *
 * @component
 * @param {FiltersProps} props - The component props
 * @param {string} props.searchText - The current search text input (currently unused but part of interface)
 * @param {[number, number]} props.distance - Current distance range values [min, max]
 * @param {(value: [number, number]) => void} props.setDistance - Callback function to update distance range
 * @param {string[]} props.categoryOptions - Array of category options for the dropdown
 * @param {string[]} props.conditionOptions - Array of condition options for the dropdown
 * @param {string} [props.dropdownClass] - Optional CSS class for dropdown styling
 * @param {string} [props.sliderClass] - Optional CSS class for slider styling
 *
 * @example
 * ```tsx
 * <Filters
 *   searchText=""
 *   distance={[0, 50]}
 *   setDistance={(newDistance) => setDistance(newDistance)}
 *   categoryOptions={["Electronics", "Furniture", "Clothing"]}
 *   conditionOptions={["New", "Like New", "Good", "Fair"]}
 * />
 * ```
 *
 * @returns {JSX.Element} Rendered filter controls
 */

